'use client';

import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import Image from '@/components/Image';
import Link from 'next/link';
import { useEffect } from 'react';

interface CandyProductsProps {
  pageData: any;
  productList: any[];
}

export default function CandyProducts({ pageData, productList = [] }: CandyProductsProps) {
  const title = pageData?.product?.title || '';
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);

  // 获取会员背景色
  const getMemberBackgroundColor = (level: number) => {
    const colors = {
      0: '#CAD8FC',
      1: '#B2CEF0',
      2: '#F7BD9E',
      3: 'linear-gradient(90deg, #C693FF 0%, #CBEAFF 100%)',
    };
    return colors[level as keyof typeof colors] || '#CAD8FC';
  };

  useEffect(() => {
    if (pageData && Object.keys(pageData).length > 0 && productList.length > 0) {
      // 延迟初始化，确保DOM完全渲染
      const initTimer = setTimeout(() => {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const productGrid = document.querySelector('.product-list .product-grid');
                if (productGrid) {
                  setTimeout(() => {
                    productGrid.classList.add('fadeInUpAnimate');
                  }, 500);
                }
                observer.disconnect();
              }
            });
          },
          {
            threshold: 0.1,
          },
        );

        const anchor = document.querySelector('.candy_product_list_anchor');
        if (anchor) {
          observer.observe(anchor);
        }

        return () => observer.disconnect();
      }, 1500);

      return () => clearTimeout(initTimer);
    }
  }, [pageData, productList]);

  // 调试商品数据结构
  logger.log(
    '商品列表详情:',
    productList.map((item, index) => ({
      index,
      id: item?.id,
      shop_sku: item?.shop_sku,
      short_name: item?.short_name,
      product_image: item?.product_image,
      hasImage: !!item?.product_image,
    })),
  );

  return (
    <div className="product-list dmp-product bg-[#0f0f0f] w-full">
      {/* 响应式标题 */}
      <h1 className="title font-bb text-white text-center text-[80px] md:text-[120px] lg:text-[140px] xl:text-[150px] leading-none flex items-center justify-center">
        {title || 'CANDY GLASSES'}
      </h1>

      {/* 商品列表 - 第一行3个，第二行2个 */}
      {Array.isArray(productList) && productList.length > 0 ? (
        <div className="mt-[40px] md:mt-[50px] lg:mt-[55px] xl:mt-[60px] relative">
          <div className="candy_product_list_anchor absolute top-0 left-0 h-[20px] w-[1px]" />

          {/* 商品网格容器 - 添加动画类 */}
          <div className="product-grid opacity0">
            {/* 第一行：3个小商品 */}
            <div className="flex justify-between gap-[3px] mb-[3px]">
              {productList.slice(0, 3).map((item: any, index: number) => {
                logger.log(`商品 ${index + 1}:`, item);

                // 更宽松的检查条件
                if (!item) {
                  logger.log(`商品 ${index + 1} 为空`);
                  return null;
                }

                const productPrice = priceInfo(userInfo, item, currency) || {
                  price: 0,
                  sell_price: 0,
                };

                return (
                  <Link
                    key={item.id || index}
                    href={`/goods-detail/${item.shop_sku || '#'}`}
                    className="li-box relative cursor-pointer block flex-1"
                    style={{ maxWidth: 'calc(33.333% - 2px)' }}
                  >
                    {/* 会员标签 */}
                    {userInfo?.is_member === 1 &&
                      item[`discount_percent_v${userInfo.member_level}`] > 0 && (
                        <div
                          className="member absolute rounded-[4px] px-[6px] text-[#282828] text-[9px] md:text-[10px] lg:text-[11px] h-[14px] md:h-[15px] lg:h-[16px] leading-[14px] md:leading-[15px] lg:leading-[16px] top-[8px] md:top-[9px] lg:top-[10px] right-[15px] md:right-[18px] lg:right-[20px]"
                          style={{ background: getMemberBackgroundColor(userInfo.member_level) }}
                        >
                          V{userInfo.member_level}
                        </div>
                      )}

                    {/* 商品图片 */}
                    <Image
                      src={
                        item.product_image || 'https://via.placeholder.com/638x400?text=No+Image'
                      }
                      alt={item.name || item.short_name || 'Product'}
                      width={638}
                      height={400}
                      className="block w-full h-auto"
                      onError={(e) => {
                        logger.log(`图片加载失败: ${item.product_image}`);
                        e.currentTarget.src =
                          'https://via.placeholder.com/638x400?text=Image+Error';
                      }}
                    />

                    {/* 底部信息区域 */}
                    <div className="bottom-area absolute left-0 w-full box-border flex justify-between items-center h-[24px] md:h-[28px] lg:h-[30px] xl:h-[32px] px-[12px] md:px-[14px] lg:px-[16px] pr-[20px] md:pr-[24px] lg:pr-[27px] bottom-[8px] md:bottom-[10px] lg:bottom-[14px] xl:bottom-[28px]">
                      {/* 商品名称 */}
                      <span className="short_name font-bb text-black text-[16px] sm:text-[18px] md:text-[20px] lg:text-[24px] xl:text-[26px] 2xl:text-[36px] truncate max-w-[60%]">
                        {item.short_name || item.name || `商品 ${index + 1}`}
                      </span>

                      {/* 价格区域 */}
                      <div className="price notranslate flex items-center">
                        {productPrice.sell_price !== productPrice.price && (
                          <span className="old-price text-[#999999] line-through mr-[8px] md:mr-[12px] lg:mr-[14px] xl:mr-[16px] text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px] xl:text-[22px] 2xl:text-[24px]">
                            {formatPriceNumberWithSymbol(productPrice.price, currency)}
                          </span>
                        )}
                        <span className="now-price text-black text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px] xl:text-[22px] 2xl:text-[24px]">
                          {formatPriceNumberWithSymbol(productPrice.sell_price, currency)}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* 第二行：2个大商品 */}
            {productList.length > 3 && (
              <div className="flex justify-between gap-[3px]">
                {productList.slice(3, 5).map((item: any, index: number) => {
                  if (!item) {
                    return null;
                  }

                  const productPrice = priceInfo(userInfo, item, currency) || {
                    price: 0,
                    sell_price: 0,
                  };
                  const actualIndex = index + 3; // 真实索引

                  return (
                    <Link
                      key={item.id || actualIndex}
                      href={`/goods-detail/${item.shop_sku || '#'}`}
                      className="li-box relative cursor-pointer block flex-1"
                      style={{ maxWidth: 'calc(50% - 1.5px)' }}
                    >
                      {/* 会员标签 */}
                      {userInfo?.is_member === 1 &&
                        item[`discount_percent_v${userInfo.member_level}`] > 0 && (
                          <div
                            className="member absolute rounded-[4px] px-[6px] text-[#282828] text-[9px] md:text-[10px] lg:text-[11px] h-[14px] md:h-[15px] lg:h-[16px] leading-[14px] md:leading-[15px] lg:leading-[16px] top-[8px] md:top-[9px] lg:top-[10px] right-[15px] md:right-[18px] lg:right-[20px]"
                            style={{ background: getMemberBackgroundColor(userInfo.member_level) }}
                          >
                            V{userInfo.member_level}
                          </div>
                        )}

                      {/* 商品图片 */}
                      <Image
                        src={
                          item.product_image || 'https://via.placeholder.com/958x400?text=No+Image'
                        }
                        alt={item.name || item.short_name || 'Product'}
                        width={958}
                        height={400}
                        className="block w-full h-auto"
                        onError={(e) => {
                          logger.log(`大图片加载失败: ${item.product_image}`);
                          e.currentTarget.src =
                            'https://via.placeholder.com/958x400?text=Image+Error';
                        }}
                      />

                      {/* 底部信息区域 */}
                      <div className="bottom-area absolute left-0 w-full box-border flex justify-between items-center h-[24px] md:h-[28px] lg:h-[30px] xl:h-[32px] px-[12px] md:px-[14px] lg:px-[16px] pr-[20px] md:pr-[24px] lg:pr-[27px] bottom-[8px] md:bottom-[10px] lg:bottom-[14px] xl:bottom-[28px]">
                        {/* 商品名称 */}
                        <span className="short_name font-bb text-black text-[16px] sm:text-[18px] md:text-[20px] lg:text-[24px] xl:text-[26px] 2xl:text-[36px] truncate max-w-[60%]">
                          {item.short_name || item.name}
                        </span>

                        {/* 价格区域 */}
                        <div className="price notranslate flex items-center">
                          {productPrice.sell_price !== productPrice.price && (
                            <span className="old-price text-[#999999] line-through mr-[8px] md:mr-[12px] lg:mr-[14px] xl:mr-[16px] text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px] xl:text-[22px] 2xl:text-[24px]">
                              {formatPriceNumberWithSymbol(productPrice.price, currency)}
                            </span>
                          )}
                          <span className="now-price text-black text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px] xl:text-[22px] 2xl:text-[24px]">
                            {formatPriceNumberWithSymbol(productPrice.sell_price, currency)}
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-white text-center p-8">
          <p className="text-xl">暂无商品数据</p>
          <p className="text-sm mt-2">商品数量: {productList?.length || 0}</p>
        </div>
      )}
    </div>
  );
}
